import { Button, Col, Form, Input, Modal, Row, Select, Spin } from 'antd'
import { Controller, useForm } from 'react-hook-form'
import * as React from 'react'
import { useEffect, useRef } from 'react'
import { debounce } from 'lodash'
import {
  $gateways,
  createDeposit,
  createDepositMounted,
  gatewaysListMounted,
  getGateways,
} from './model'
import { useUnit } from 'effector-react/effector-react.mjs'
import { useNavigate } from 'react-router-dom'
import { path } from '../../../../libs'

type FormData = {
  clientId: string
  gatewayId: number | null
  initialAmount?: string
  clientAmount?: string
  comment: string
  internalComment: string
}

export const CreateDepositNovaForm = () => {
  const isCreateDepositLoading = useUnit(createDeposit.pending)

  const gateways = useUnit($gateways)
  const isGatewaysLoading = useUnit(getGateways.pending)

  const navigate = useNavigate()

  const { watch, handleSubmit, setError, clearErrors, control } =
    useForm<FormData>({
      mode: 'onChange',
      defaultValues: {
        clientId: '',
        gatewayId: null,
        initialAmount: '',
        clientAmount: '',
        comment: '',
        internalComment: '',
      },
    })

  const initialAmount = watch('initialAmount')
  const clientAmount = watch('clientAmount')

  const debounceGatewaysSearch = useRef(
    debounce((searchTerm: string) => {
      if (searchTerm) {
        gatewaysListMounted({
          searchText: {
            internalNameAndId: searchTerm,
          },
        })
      }
    }, 1000),
  )

  useEffect(() => {
    return () => {
      debounceGatewaysSearch.current.cancel()
    }
  }, [])

  const onSubmit = (data: FormData) => {
    if (!data.gatewayId) {
      setError('gatewayId', {
        type: 'required',
        message: 'Gateway обязателен',
      })
      return
    }

    Modal.confirm({
      title: 'Are you ready?',
      onOk: () => {
        createDepositMounted({
          clientId: Number(data.clientId),
          gatewayId: Number(data.gatewayId),
          initialAmount: Boolean(initialAmount) ? initialAmount : undefined,
          clientAmount: Boolean(clientAmount) ? clientAmount : undefined,
          comment: data.comment,
          internalComment: data.internalComment,
        })
      },
    })
  }

  useEffect(() => {
    if (!initialAmount || !clientAmount) {
      clearErrors('initialAmount')
      clearErrors('clientAmount')
    }
  }, [initialAmount, clientAmount])

  useEffect(() => {
    const unsubscribe = createDeposit.done.watch(() => {
      navigate(path.billing.balanceOperation.entry())
    })
    return unsubscribe
  }, [navigate])

  return (
    <div className="pt-3">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="clientId"
              control={control}
              rules={{ required: 'Client ID обязателен' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Client ID"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input {...field} type="number" />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="gatewayId"
              control={control}
              rules={{
                required: 'Gateway обязателен',
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Gateway"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Select
                    {...field}
                    showSearch
                    filterOption={false}
                    loading={isGatewaysLoading}
                    notFoundContent={
                      isGatewaysLoading ? <Spin size="small" /> : null
                    }
                    style={{ width: '100%' }}
                    options={gateways?.map((item) => ({
                      label: `${item.gatewayId} ${item.internalName}`,
                      value: item.gatewayId,
                    }))}
                    onSearch={(text) => {
                      debounceGatewaysSearch.current(text)
                    }}
                    onChange={(value) => {
                      field.onChange(value)
                      debounceGatewaysSearch.current('')
                    }}
                  />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={8}>
            <Controller
              name="initialAmount"
              control={control}
              rules={{
                validate: (value) => {
                  if (value === '0' && !clientAmount) {
                    return 'Значение не должно быть равно нулю'
                  }
                  if (value && clientAmount) {
                    return 'Значения client amount и initial amount не должны быть заполнены одновременно'
                  }
                  return true
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Initial Amount"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input {...field} />
                </Form.Item>
              )}
            />
          </Col>
          <Col span={8}>
            <Controller
              name="clientAmount"
              control={control}
              rules={{
                validate: (value) => {
                  if (value === '0' && !initialAmount) {
                    return 'Значение не должно быть равно нулю'
                  }
                  if (value && initialAmount) {
                    return 'Значения client amount и initial amount не должны быть заполнены одновременно'
                  }
                  return true
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Client Amount"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input {...field} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={16}>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <Form.Item label="Comment">
                  <Input.TextArea {...field} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={16}>
            <Controller
              name="internalComment"
              control={control}
              rules={{ required: 'Internal comment обязателен' }}
              render={({ field, fieldState: { error } }) => (
                <Form.Item
                  label="Internal comment"
                  validateStatus={error ? 'error' : undefined}
                  help={error?.message}
                >
                  <Input.TextArea {...field} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={16} className="d-flex justify-content-end">
            <Button
              type="primary"
              htmlType="submit"
              disabled={isCreateDepositLoading}
              loading={isCreateDepositLoading}
            >
              Create
            </Button>
          </Col>
        </Row>
      </form>
    </div>
  )
}
